import {createSelector} from '@reduxjs/toolkit';
import createCachedSelector from 're-reselect';
import {getTotalBuildings} from '../../data/buildings/selectors';
import {getAllCalendarWeeks, getCalendarDayEntities} from '../../data/calendar/selectors';
import {getConnectedUsersGroupedByWorkspaceReservationDate} from '../../data/connections/selectors';
import {getAreAllNodesClosed} from '../../data/openingDays/selectors';
import {getAccessProfile} from '../../data/profiles/selectors';
import {
  getWorkspaceReservationByDate,
  getWorkspaceReservationCountByWeekNumber,
} from '../../data/workspaceReservation/selectors';
import {getStartOfTodayISOString} from '../../data/settings/selectors';
import {getAllConnectionsCheckInStatus} from '../../data/checkIn/selectors';
import {getAreAllNodesFull, getNodeInformationByNodeId} from '../nodeInformation';
import {RootState} from '../../rootReducer';
import {toUICalendarWeeks} from './mappers';
import {byProp, dayNamesToISODays, formatDateAsKey, isBeforeToday, isoDaysContainsDate} from '@lib/utils';
import {CheckInEventType} from '@lib/infrastructure';
import addDays from 'date-fns/addDays';
import isWithinInterval from 'date-fns/isWithinInterval';
import parseISO from 'date-fns/parseISO';
import getISOWeek from 'date-fns/getISOWeek';
import {findParkingReservationByDate, getAllParkingReservations} from '../../data/parkingReservation';
import {getWorkdayByDate} from '../../data/workday';
import {cachedComplexProps, cachedDate} from '../../util';

export const getCalendar = createSelector(
  getAllCalendarWeeks,
  getCalendarDayEntities,
  getStartOfTodayISOString,
  (allWeeks, dayEntities, todayISOString) => {
    return toUICalendarWeeks(allWeeks, dayEntities, todayISOString);
  },
);

export const getIsLoadingCalendar = (state: RootState) => state.ui.calendar.isLoading;

export const getIsLoadingCalendarState = (state: RootState) => state.ui.calendar.loadingState;

export const getIsRefreshingCalendar = (state: RootState) => state.ui.calendar.isRefreshing;

export const getCalendarLastLoadedTimestamp = (state: RootState) => state.ui.calendar.timestamp;

export const getIsCalendarBusy = createSelector(
  getIsLoadingCalendar,
  getIsRefreshingCalendar,
  (loading, refreshing) => loading || refreshing,
);

export const getWeekQuotaByWeekNumber = createCachedSelector(
  getAccessProfile,
  getWorkspaceReservationCountByWeekNumber,
  ({daysPerWeek: total}, count) => ({count, total}),
)((_state, weekNumber) => `${weekNumber}`);

export const getDayState = createCachedSelector(
  getWorkspaceReservationByDate,
  getAreAllNodesClosed,
  getAreAllNodesFull,
  (
    workspaceReservation,
    allNodesClosed,
    allNodesFull,
  ): {localize: true; result: 'Closed' | 'Full'} | {localize: false; result: string} => {
    if (workspaceReservation && workspaceReservation.createdBy && !workspaceReservation.groupReservationId)
      return {localize: false, result: workspaceReservation.createdBy};
    if (allNodesClosed) return {localize: true, result: 'Closed'};
    if (allNodesFull) return {localize: true, result: 'Full'};
    return {localize: false, result: ''};
  },
)((_state, date) => date?.toISOString() || '');

export const getConnectionAvatarsDataByDate = createCachedSelector(
  getConnectedUsersGroupedByWorkspaceReservationDate,
  getAllConnectionsCheckInStatus,
  (_state: RootState, date: Date) => date,
  (connectedUsersByWorkspaceReservationDate, checkInStatus, date) => {
    const key = formatDateAsKey(date);
    const connectedUsers = connectedUsersByWorkspaceReservationDate[key] ?? [];

    const atTheOffice = connectedUsers
      .slice()
      .filter((user) => checkInStatus.find((st) => st.userId === user.id)?.eventType === CheckInEventType.AtTheOffice)
      .sort(byProp('name'));
    const restOfUsers = connectedUsers
      .filter((user) => !atTheOffice.some((atoUser) => atoUser.id === user.id))
      .sort(byProp('name'));
    return atTheOffice.concat(restOfUsers);
  },
)((_state, date) => date?.toISOString() || '');

export const getIsWorkspaceReservationButtonEnabled = createCachedSelector(
  [
    getStartOfTodayISOString,
    getAccessProfile,
    getTotalBuildings,
    getWorkspaceReservationByDate,
    getAreAllNodesClosed,
    getAreAllNodesFull,
    (state: RootState, date: Date) => {
      const info = getWeekQuotaByWeekNumber(state, getISOWeek(date));
      return cachedComplexProps(info, (i) => `${i.count}_${i.total}`);
    },
    (state: RootState, date: Date) =>
      getNodeInformationByNodeId(state, {date, nodeId: getWorkdayByDate(state, date.toISOString())?.nodeId}),
    (_state: RootState, date: Date) => cachedDate(date),
  ],
  (
    todayISOString,
    {daysAhead, registrationDays},
    buildingCount,
    workspaceReservation,
    areAllBuildingsClosed,
    areAllNodesFull,
    workspaceReservationQuota,
    {isFull: isWorkdayOfficeFull},
    date,
  ) => {
    if (!date) {
      return false;
    }

    const today = parseISO(todayISOString);
    const lastBookableDay = addDays(today, daysAhead);
    const isPast = isBeforeToday(date, today);
    const registrationISODays = dayNamesToISODays(registrationDays);
    const isDayAllowedDay = isoDaysContainsDate(date)(registrationISODays);
    const isEnabled =
      (!isPast && !!workspaceReservation) ||
      (isWithinInterval(date, {start: today, end: lastBookableDay}) &&
        workspaceReservationQuota.count < workspaceReservationQuota.total &&
        buildingCount > 0 &&
        !areAllBuildingsClosed &&
        !areAllNodesFull &&
        isDayAllowedDay &&
        !isWorkdayOfficeFull);
    return isEnabled;
  },
)((_state: RootState, date) => date?.toISOString() || '');

export const getTodayCalendarHeaderData = createCachedSelector(
  getWorkspaceReservationByDate,
  (workspaceReservation) => {
    if (!workspaceReservation) return {};
    const {id, buildingId, buildingName, floorName, areaName, deskName} = workspaceReservation;
    return {
      id,
      buildingId,
      buildingName,
      floorName,
      areaName,
      deskName,
      location: [floorName, areaName].filter((text) => !!text).join(', '),
    };
  },
)((_state, date?: Date) => date?.toISOString() || '');

export const getFirstDateWithoutParkingReservation = createSelector(
  getCalendar,
  getAllParkingReservations,
  getStartOfTodayISOString,
  (calendarItemsGroupedByWeek, parkingReservations, todayISOString) => {
    const thisWeekCalendarItems = calendarItemsGroupedByWeek.find(
      (calendarItemGroup) => calendarItemGroup.weekType === 'thisWeek',
    );
    if (!thisWeekCalendarItems) {
      return undefined;
    }
    const todayDate = parseISO(todayISOString);
    const calendarItemWithoutParking = thisWeekCalendarItems.data.find((calendarItem) => {
      const date = calendarItem.date;
      return !isBeforeToday(parseISO(date), todayDate) && !findParkingReservationByDate(parkingReservations, date);
    });
    return calendarItemWithoutParking?.date;
  },
);

export const getLastCalendarDayAsDate = createSelector(getCalendarDayEntities, (days) => {
  const daysArr = Object.values(days);

  const lastDay = daysArr[daysArr.length - 1];

  return lastDay ? parseISO(lastDay.date) : null;
});
